import { useWindowTitle } from '@cibo/profile'
import { usePrograms } from '@cibo/programs'
import { Alert, Box, Divider, Button, Typography } from '@mui/material'
import { EnrollmentList } from 'components'
import { LayoutSection } from 'components/ImpactLayout'
import { useTranslation } from 'react-i18next'
import { ProgramModel } from '@cibo/core'
import { useMemo, useState } from 'react'
import { partition } from 'ramda'
import { EnrollmentWindowClosedModal } from '@cibo/programs'
import { EnrollmentsTable } from './EnrollmentsTable'
import { useEnrollments } from './useEnrollments'

export const Enrollments = () => {
  const { t } = useTranslation('impact/pages/Enrollments')
  const [enrollmentClosedModalOpen, setEnrollmentClosedModalOpen] = useState(false)
  useWindowTitle(t('windowTitle'))
  const programs = usePrograms()
  const { sortedEnrollmentsWithContent: items, data, isLoading, isFetching } = useEnrollments()
  const closedPrograms = useMemo(
    () =>
      partition<ProgramModel>(
        (program?: ProgramModel) => !!program && program.isClosedForEnrollment,
        programs.data || []
      )[0],
    [programs.isFetching]
  )
  const closedProgramIds = useMemo(() => closedPrograms.map(program => program.programId), [
    closedPrograms,
  ])

  const enrollmentsInClosedPrograms = useMemo(
    () =>
      items &&
      items
        .filter(enrollment => closedProgramIds.includes(enrollment.programId))
        .sort((a, b) => {
          if (a.programId > b.programId) return -1
          if (a.programId < b.programId) return 1
          return 0
        }),
    [isFetching, closedPrograms, items]
  )

  return (
    <>
      <LayoutSection container>
        {enrollmentsInClosedPrograms && enrollmentsInClosedPrograms.length > 0 && (
          <>
            <Alert
              severity="warning"
              action={
                data?.numItems && data?.numItems > 0 ? (
                  <Button color="warning" onClick={() => setEnrollmentClosedModalOpen(true)}>
                    {t('moreInfo')}
                  </Button>
                ) : null
              }
            >
              {t('invalidEnrollments', { count: enrollmentsInClosedPrograms.length })}
            </Alert>
            <Divider />
          </>
        )}
        <Box>
          <EnrollmentsTable />
        </Box>
      </LayoutSection>
      <EnrollmentWindowClosedModal
        maxWidth="md"
        fullWidth
        destructive
        modal={false}
        open={enrollmentClosedModalOpen}
        onClose={() => setEnrollmentClosedModalOpen(false)}
        onDeleteSuccess={() => setEnrollmentClosedModalOpen(false)}
      >
        <Typography>
          {t('enrollmentsDelete', {
            context: closedPrograms.length === 1 ? 'singleProgram' : 'multiplePrograms',
            count: enrollmentsInClosedPrograms.length,
          })}
        </Typography>
        <EnrollmentList
          loading={isLoading}
          rows={enrollmentsInClosedPrograms}
          pagination
          hideFooter={false}
        />
      </EnrollmentWindowClosedModal>
    </>
  )
}
